import React from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import { useField } from "../form/Form";
import ErrorsForField from "../form/ErrorsForField";
import Icon from "../icons/Icon";
import { createDataAttributes } from "../../utilities";
import LoadingIndicator from "../loading-indicator/LoadingIndicator";
import Label from "./Label";
import "./InputSearch.scss";

const SearchField = ({
  id,
  name,
  label,
  labelElement,
  placeholder,
  value,
  maxLength,
  className,
  disabled,
  onChange,
  onBlur,
  onKeyDown,
  onValidate,
  required,
  size,
  displayIcon,
  hideLabel,
  noLabel,
  fieldErrors,
  hideErrors,
  requiredError,
  dataAttrib,
  dataWrapperAttrib,
  isSearching,
  ...rest
}) => {
  useField({ name, validation: { required, requiredError }, onValidate });

  const sizeClass = size ? "uikit__input-size__" + size : null;
  const containerClasses = classnames(
    "uikit__form-item",
    "uikit__search-input-wrapper",
    {
      "uikit__search-input__disabled": disabled,
      "uikit__hide-label": hideLabel,
      "uikit__form__hide-errors": hideErrors
    },
    className,
    sizeClass
  );
  const inputClass = classnames("uikit__search-input", {
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value
  });

  const handleChange = event => {
    onChange({ event, field: name, value: event.currentTarget.value });
  };

  const handleBlur = event => {
    onBlur({ event, field: name, value: event.currentTarget.value });
  };

  const handleKeyDown = event => {
    if (onKeyDown) {
      onKeyDown({ event, value: event.currentTarget.value });
    }
  };

  return (
    <div
      className={containerClasses}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      {!noLabel && (
        <Label
          htmlFor={id}
          label={label}
          required={required}
          labelElement={
            <span
              className={classnames({
                "inline-flex": !!labelElement && !!isSearching
              })}
            >
              {labelElement}
              {isSearching && (
                <LoadingIndicator
                  className="uikit__search-input__searching-spinner"
                  type="spinner"
                  message="Searching"
                />
              )}
            </span>
          }
        />
      )}
      <div className="uikit__search__inner">
        <input
          type="text"
          id={id}
          name={name}
          value={value}
          className={inputClass}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          {...createDataAttributes(dataAttrib)}
          {...rest}
          aria-describedby={`errors_for_${id}`}
        />
        {displayIcon && !isSearching && (
          <button
            className="uikit__search__buttons"
            disabled={disabled}
            type="submit"
          >
            <span className="uikit-sr-only">Search</span>
            <Icon icon="fasSearch" />
          </button>
        )}
        {isSearching && (
          <div className="searching">
            <span className="uikit-sr-only">Loading</span>
            <LoadingIndicator type="spinner" message="" />
          </div>
        )}
      </div>
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

SearchField.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.string.isRequired,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,
  isSearching: propTypes.bool,
  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific
  maxLength: propTypes.number,
  placeholder: propTypes.string,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fill"
  ]),
  displayIcon: propTypes.bool,
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object)
};

SearchField.defaultProps = {
  placeholder: "Search",
  maxLength: 250,
  required: false,
  size: "full",
  displayIcon: true,
  noLabel: false,
  hideErrors: false,
  componentname: "SearchField"
};

export default SearchField;
